import {isFeatureEnabled} from 'feature-flags';
import {
  showNotification,
  openDialog,
  copyToClipboard,
  DropdownTag,
  Flag,
  Action,
} from 'platform/components';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  useGetCurrentUserInfoQuery,
  useLazyGetUserInvitationQuery,
  useReinviteUserMutation,
} from '@dms/api/customer';
import {UserResponseBody} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {handleApiError, isSystemUser} from '@dms/shared';

import {buildArray, useNavigate} from 'shared/web';

import {DeactivateUserDialogContent} from '../../../components/DeactivateUserDialogContent/DeactivateUserDialogContent';
import {EditUserPasswordForm} from '../components/EditUserPasswordForm';
import {MANAGER_ROLE_NAME} from '../constants/MANAGER_ROLE_NAME';
import {useUserActivationDropdownParams} from './useUserActivationDropdownParams';

export const useSettingsHeaderActions = (user: UserResponseBody | undefined): Action[] => {
  const {data: currentUser} = useGetCurrentUserInfoQuery();
  const [reinviteUser, {isLoading: isReinviting}] = useReinviteUserMutation();
  const [getUserInvitation, {isFetching: isFetchingInvitation}] = useLazyGetUserInvitationQuery();
  const navigate = useNavigate();

  const {colorScheme, dropdownItems, label, isLoading} = useUserActivationDropdownParams(user);

  const handleChangePassword = () => {
    if (!user?.id) {
      showNotification.error();
      throw new Error('User ID is required');
    }

    openDialog(
      <EditUserPasswordForm
        userId={user?.id}
        data-testid={testIds.settings.userManagementDetailV2('EditUserPassword')}
      />,
      {
        title: i18n.t('entity.user.labels.changePassword'),
      }
    );
  };

  const isUserAdmin = isNil(currentUser?.roles)
    ? false
    : currentUser.roles.some((role) => role.title === MANAGER_ROLE_NAME);

  const handleDeleteUser = () => {
    if (isNilOrEmpty(user?.id)) {
      return;
    }
    openDialog(
      <DeactivateUserDialogContent
        userId={user!.id}
        onSuccess={() => {
          navigate(settingsRoutes.userManagement);
        }}
      />,
      {
        id: 'deleteUserDialog',
        'data-testid': 'deleteUserDialog',
        title: i18n.t('entity.deactivateUserDialog.title'),
      }
    );
  };

  const isUserInvited = user?.state === 'invited';
  const handleReinviteUser = () => {
    if (isNilOrEmpty(user?.id)) {
      return;
    }

    reinviteUser({userId: user!.id})
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.invitationSent'));
        navigate(settingsRoutes.userManagement);
      })
      .catch(handleApiError);
  };

  const handleCopyInvitationLink = () => {
    if (isNilOrEmpty(user?.id)) {
      return;
    }

    getUserInvitation({userId: user!.id})
      .unwrap()
      .then((invitation) => copyToClipboard(invitation.invitationUrl))
      .catch(handleApiError);
  };

  const isCopyInvitationLinkVisible =
    isUserInvited && isFeatureEnabled(featureFlags.COPY_INVITATION_LINK);

  if (!user) {
    return [];
  }

  if (isSystemUser(user)) {
    return [
      {
        type: 'custom',
        component: (
          <Flag
            colorScheme="blue"
            isSubtle
            size="large"
            label={i18n.t('entity.user.labels.systemUser')}
            data-testid={testIds.settings.userManagementDetailV2('systemUserBadge')}
          />
        ),
      },
    ];
  }

  return buildArray<Action>([
    {
      type: 'button',
      title: i18n.t('page.login.actions.resetPassword'),
      variant: 'ghostLink',
      leftIcon: 'action/lock',
      onClick: handleChangePassword,
    },
  ])
    .when(isCopyInvitationLinkVisible, {
      type: 'button',
      title: i18n.t('general.actions.copyInvitationLink'),
      variant: 'ghostLink',
      isLoading: isFetchingInvitation,
      leftIcon: 'content/link',
      onClick: handleCopyInvitationLink,
      'data-testid': testIds.settings.userManagementDetailV2('copyInvitationLink'),
    })
    .when(isUserInvited, {
      type: 'button',
      title: i18n.t('general.actions.reinvite'),
      variant: 'ghostLink',
      isLoading: isReinviting,
      leftIcon: 'navigation/refresh',
      onClick: handleReinviteUser,
    })
    .whenNot(isUserInvited, {
      type: 'custom',
      component: (
        <DropdownTag
          dropdownItems={dropdownItems}
          label={label}
          isLoading={isLoading}
          colorScheme={colorScheme}
          size="large"
        />
      ),
    })
    .when(isUserAdmin && !isUserInvited, {
      type: 'button',
      title: i18n.t('general.actions.delete'),
      variant: 'danger',
      leftIcon: 'action/delete',
      onClick: handleDeleteUser,
    });
};
